// src/lib/whatIfService.ts
//
// BekalNanti — Milestone P9: What-If / Scenario Authority
// Pure, deterministic simulation of planning scenarios.
// BASELINE IS NEVER MUTATED.
// All financial projection delegates strictly to canonical P5 calculation & P6 reality check.

import { GoalRecord, JourneyData } from '../journey/types'
import { runGoalCalculation } from './calculationService'
import { evaluatePlanReality } from './realityCheckService'
import { WhatIfScenarioModifier, WhatIfComparisonResult } from './whatIfTypes'

/**
 * Simulates a What-If scenario against baseline goal planning data.
 * Pure function: does NOT alter existing records or state.
 */
export function simulateWhatIfScenario(
  goal: GoalRecord,
  journey: JourneyData,
  modifier: WhatIfScenarioModifier
): WhatIfComparisonResult {
  // 1. Run baseline calculation & reality check
  const baselineP5 = runGoalCalculation(goal, journey)
  const baselineP6 = evaluatePlanReality(goal, journey)

  const baselineYear = goal.target?.targetYear || null
  const baselineCost = baselineP5?.costs?.totalProjectedCost || 0
  const baselineMonthly = baselineP5?.savingRequirement?.requiredMonthlySavingConfirmedGap || 0
  const baselineStatus = baselineP6.status
  const baselineStatusLabel = baselineP6.statusLabel
  const baselineCapDiff = baselineP6.snapshot.monthlyCapacityDifference

  // If baseline cannot be calculated, return early
  if (!baselineP5 || !baselineP5.isCalculable) {
    return {
      baseline: {
        targetYear: baselineYear,
        futureTotalCost: baselineCost,
        requiredMonthlySaving: baselineMonthly,
        realityStatus: baselineStatus,
        realityLabel: baselineStatusLabel,
        monthlyCapacityDifference: baselineCapDiff,
      },
      scenario: {
        targetYear: modifier.targetHorizonYear ?? baselineYear,
        futureTotalCost: baselineCost,
        requiredMonthlySaving: baselineMonthly,
        realityStatus: baselineStatus,
        realityLabel: baselineStatusLabel,
        monthlyCapacityDifference: baselineCapDiff,
      },
      delta: {
        costDifference: 0,
        monthlySavingDifference: 0,
        yearsDifference: 0,
        statusChanged: false,
        improved: false,
      },
      p5ScenarioResult: null,
      p6ScenarioResult: null,
    }
  }

  // 2. Clone and modify Goal & Journey for SCENARIO ONLY (Deep copy to ensure purity)
  const scenarioGoal: GoalRecord = JSON.parse(JSON.stringify(goal))
  const scenarioJourney: JourneyData = JSON.parse(JSON.stringify(journey))

  // Apply Year Modifier
  if (modifier.targetHorizonYear && scenarioGoal.target) {
    scenarioGoal.target.targetYear = modifier.targetHorizonYear
  }

  // Apply Cost Adjustments (tuition / living cost adjustments)
  if (scenarioGoal.costItems && (modifier.tuitionAdjustmentPercent || modifier.livingCostAdjustmentPercent)) {
    scenarioGoal.costItems = scenarioGoal.costItems.map((item: any) => {
      let multiplier = 1
      if (item.category === 'tuition' && modifier.tuitionAdjustmentPercent) {
        multiplier += modifier.tuitionAdjustmentPercent / 100
      } else if ((item.category === 'living' || item.category === 'living_cost') && modifier.livingCostAdjustmentPercent) {
        multiplier += modifier.livingCostAdjustmentPercent / 100
      }
      return {
        ...item,
        amount: item.amount !== null ? Math.round(item.amount * Math.max(0, multiplier)) : null,
      }
    })
  }

  // Apply Additional One-time Funding
  if (modifier.additionalOneTimeFunding && modifier.additionalOneTimeFunding > 0) {
    const additionalFunding: any = {
      id: `sim-funding-${Date.now()}`,
      goalId: goal.id,
      name: 'Simulasi Bantuan / Beasiswa Tambahan',
      sourceType: 'scholarship',
      amount: modifier.additionalOneTimeFunding,
      currency: 'IDR',
      status: 'confirmed',
      isConfirmed: true,
      certainty: 'known',
    }
    scenarioGoal.fundingSources = [...(scenarioGoal.fundingSources || []), additionalFunding]
  }

  // Apply Additional Monthly Contribution (Increases net monthly capacity in scenario journey)
  if (modifier.additionalMonthlyContribution && modifier.additionalMonthlyContribution > 0 && scenarioJourney.financialReality?.summary) {
    scenarioJourney.financialReality.summary.netMonthlyCapacity += modifier.additionalMonthlyContribution
  }

  // 3. Delegate strictly to CANONICAL P5 & P6 for scenario projection
  const scenarioP5 = runGoalCalculation(scenarioGoal, scenarioJourney)
  const scenarioP6 = evaluatePlanReality(scenarioGoal, scenarioJourney)

  const scenarioYear = scenarioGoal.target?.targetYear || baselineYear
  const scenarioCost = scenarioP5?.costs?.totalProjectedCost || baselineCost
  const scenarioMonthly = scenarioP5?.savingRequirement?.requiredMonthlySavingConfirmedGap || baselineMonthly
  const scenarioStatus = scenarioP6.status
  const scenarioStatusLabel = scenarioP6.statusLabel
  const scenarioCapDiff = scenarioP6.snapshot.monthlyCapacityDifference

  // 4. Calculate deterministic deltas
  const costDifference = scenarioCost - baselineCost
  const monthlySavingDifference = scenarioMonthly - baselineMonthly
  const yearsDifference = (scenarioYear || 0) - (baselineYear || 0)
  const statusChanged = scenarioStatus !== baselineStatus
  const improved =
    scenarioMonthly < baselineMonthly ||
    (scenarioStatus === 'manageable_commitment' && baselineStatus === 'funding_gap') ||
    (scenarioStatus === 'fully_funded' && baselineStatus !== 'fully_funded')

  return {
    baseline: {
      targetYear: baselineYear,
      futureTotalCost: baselineCost,
      requiredMonthlySaving: baselineMonthly,
      realityStatus: baselineStatus,
      realityLabel: baselineStatusLabel,
      monthlyCapacityDifference: baselineCapDiff,
    },
    scenario: {
      targetYear: scenarioYear,
      futureTotalCost: scenarioCost,
      requiredMonthlySaving: scenarioMonthly,
      realityStatus: scenarioStatus,
      realityLabel: scenarioStatusLabel,
      monthlyCapacityDifference: scenarioCapDiff,
    },
    delta: {
      costDifference,
      monthlySavingDifference,
      yearsDifference,
      statusChanged,
      improved,
    },
    p5ScenarioResult: scenarioP5,
    p6ScenarioResult: scenarioP6,
  }
}
